/**
 * AlertBadge Component
 * Displays a warning icon with tooltip showing list of alerts
 *
 * @version 2025.1.0
 */

import React from 'react';
import { Box, Tooltip, Typography, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { Warning as WarningIcon, Circle as CircleIcon } from '@mui/icons-material';

export interface Alert {
  message: string;
  severity?: 'warning' | 'error' | 'info';
}

interface AlertBadgeProps {
  alerts: Alert[];
  size?: 'small' | 'medium';
}

/**
 * AlertBadge - Shows a warning icon with alerts on hover
 *
 * Usage:
 * ```tsx
 * const alerts = [
 *   { message: 'Nenhum setor cadastrado' },
 *   { message: 'Nenhum funcionário cadastrado' },
 * ];
 * <AlertBadge alerts={alerts} />
 * ```
 */
export const AlertBadge: React.FC<AlertBadgeProps> = ({ alerts, size = 'small' }) => {
  if (!alerts || alerts.length === 0) {
    return null;
  }

  const iconSize = size === 'small' ? 18 : 22;

  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Alertas ({alerts.length})
      </Typography>
      <List dense disablePadding>
        {alerts.map((alert, index) => (
          <ListItem key={index} disablePadding sx={{ py: 0.25 }}>
            <ListItemIcon sx={{ minWidth: 20 }}>
              <CircleIcon
                sx={{
                  fontSize: 6,
                  color:
                    alert.severity === 'error'
                      ? 'error.main'
                      : alert.severity === 'info'
                      ? 'info.main'
                      : 'warning.main',
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary={alert.message}
              primaryTypographyProps={{
                variant: 'body2',
                sx: { fontSize: '0.8rem' },
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="top">
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'warning.light',
          borderRadius: 1,
          p: 0.5,
          ml: 1,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: 'warning.main',
            transform: 'scale(1.1)',
          },
        }}
      >
        <WarningIcon
          sx={{
            fontSize: iconSize,
            color: 'warning.dark',
          }}
        />
      </Box>
    </Tooltip>
  );
};

// ============================================================================
// HELPER FUNCTIONS FOR GENERATING ALERTS
// ============================================================================

export interface CompanyAlertCounts {
  departmentCount?: number;
  occupationCount?: number;
  employeeCount?: number;
  riskApplicationCount?: number;
  sourceApplicationCount?: number;
  examApplicationCount?: number;
}

export interface DepartmentAlertCounts {
  occupationCount?: number;
  employeeCount?: number;
  riskApplicationCount?: number;
  sourceApplicationCount?: number;
  examApplicationCount?: number;
}

export interface OccupationAlertCounts {
  employeeCount?: number;
  riskApplicationCount?: number;
  sourceApplicationCount?: number;
  examApplicationCount?: number;
}

export interface ApplicationAlertCounts {
  companyCount?: number;
  departmentCount?: number;
  occupationCount?: number;
}

/**
 * Generate alerts for a company based on counts
 */
export function generateCompanyAlerts(counts: CompanyAlertCounts): Alert[] {
  const alerts: Alert[] = [];

  if (counts.departmentCount !== undefined && counts.departmentCount === 0) {
    alerts.push({ message: 'Nenhum setor cadastrado' });
  }
  if (counts.occupationCount !== undefined && counts.occupationCount === 0) {
    alerts.push({ message: 'Nenhum cargo cadastrado' });
  }
  if (counts.employeeCount !== undefined && counts.employeeCount === 0) {
    alerts.push({ message: 'Nenhum funcionário cadastrado' });
  }
  if (counts.riskApplicationCount !== undefined && counts.riskApplicationCount === 0) {
    alerts.push({ message: 'Nenhum risco vinculado' });
  }
  if (counts.sourceApplicationCount !== undefined && counts.sourceApplicationCount === 0) {
    alerts.push({ message: 'Nenhuma fonte vinculada' });
  }
  if (counts.examApplicationCount !== undefined && counts.examApplicationCount === 0) {
    alerts.push({ message: 'Nenhum exame vinculado' });
  }

  return alerts;
}

/**
 * Generate alerts for a department based on counts
 */
export function generateDepartmentAlerts(counts: DepartmentAlertCounts): Alert[] {
  const alerts: Alert[] = [];

  if (counts.occupationCount !== undefined && counts.occupationCount === 0) {
    alerts.push({ message: 'Nenhum cargo cadastrado' });
  }
  if (counts.employeeCount !== undefined && counts.employeeCount === 0) {
    alerts.push({ message: 'Nenhum funcionário cadastrado' });
  }
  if (counts.riskApplicationCount !== undefined && counts.riskApplicationCount === 0) {
    alerts.push({ message: 'Nenhum risco vinculado' });
  }
  if (counts.sourceApplicationCount !== undefined && counts.sourceApplicationCount === 0) {
    alerts.push({ message: 'Nenhuma fonte vinculada' });
  }
  if (counts.examApplicationCount !== undefined && counts.examApplicationCount === 0) {
    alerts.push({ message: 'Nenhum exame vinculado' });
  }

  return alerts;
}

/**
 * Generate alerts for an occupation based on counts
 */
export function generateOccupationAlerts(counts: OccupationAlertCounts): Alert[] {
  const alerts: Alert[] = [];

  if (counts.employeeCount !== undefined && counts.employeeCount === 0) {
    alerts.push({ message: 'Nenhum funcionário cadastrado' });
  }
  if (counts.riskApplicationCount !== undefined && counts.riskApplicationCount === 0) {
    alerts.push({ message: 'Nenhum risco vinculado' });
  }
  if (counts.sourceApplicationCount !== undefined && counts.sourceApplicationCount === 0) {
    alerts.push({ message: 'Nenhuma fonte vinculada' });
  }
  if (counts.examApplicationCount !== undefined && counts.examApplicationCount === 0) {
    alerts.push({ message: 'Nenhum exame vinculado' });
  }

  return alerts;
}

/**
 * Generate alerts for risks/sources/exams based on application counts
 */
export function generateApplicationAlerts(counts: ApplicationAlertCounts): Alert[] {
  const alerts: Alert[] = [];

  if (counts.companyCount !== undefined && counts.companyCount === 0) {
    alerts.push({ message: 'Nenhuma empresa vinculada' });
  }
  if (counts.departmentCount !== undefined && counts.departmentCount === 0) {
    alerts.push({ message: 'Nenhum setor vinculado' });
  }
  if (counts.occupationCount !== undefined && counts.occupationCount === 0) {
    alerts.push({ message: 'Nenhum cargo vinculado' });
  }

  return alerts;
}

export default AlertBadge;
